export const RUNTIME_REQUEST_STATE_EVENT = 'showcase:runtime-request-state';

export type RuntimeRequestIssue =
  | 'offline'
  | 'timeout'
  | 'access_denied'
  | 'expired'
  | 'stale'
  | 'unavailable';

export type RuntimeRequestState = {
  kind: RuntimeRequestIssue | 'recovered';
  status?: number;
};

function payloadText(payload: unknown): string {
  try {
    return JSON.stringify(payload ?? '').toLocaleLowerCase('ru-RU');
  } catch {
    return '';
  }
}

export function classifyRuntimeResponse(status: number, payload?: unknown): RuntimeRequestIssue | null {
  const text = payloadText(payload);
  if (status === 401 || status === 403) return 'access_denied';
  if (
    status === 410
    || status === 419
    || text.includes('expired')
    || text.includes('истек')
    || text.includes('истёк')
  ) return 'expired';
  if (status === 408 || status === 504) return 'timeout';
  if (
    (status === 409 || status === 412)
    && (
      text.includes('stale')
      || text.includes('revision')
      || text.includes('верси')
      || text.includes('устарел')
    )
  ) return 'stale';
  if (status >= 500) return 'unavailable';
  return null;
}

export function publishRuntimeRequestState(state: RuntimeRequestState): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent<RuntimeRequestState>(RUNTIME_REQUEST_STATE_EVENT, { detail: state }));
}

export async function publishRuntimeResponse(response: Response, method: string): Promise<void> {
  if (!['GET', 'HEAD'].includes(method)) return;
  if (response.ok) {
    publishRuntimeRequestState({ kind: 'recovered', status: response.status });
    return;
  }
  let payload: unknown;
  try {
    payload = await response.clone().json();
  } catch {
    payload = undefined;
  }
  const kind = classifyRuntimeResponse(response.status, payload);
  if (kind) publishRuntimeRequestState({ kind, status: response.status });
}

export async function runtimeFetch(
  input: RequestInfo | URL,
  init: RequestInit,
  method: string,
): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  const timeoutMs = ['GET', 'HEAD'].includes(method) ? 15_000 : 30_000;
  const timeoutId = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, timeoutMs);
  const callerSignal = init.signal;
  const abortFromCaller = (): void => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) abortFromCaller();
  else callerSignal?.addEventListener('abort', abortFromCaller, { once: true });
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (!callerSignal?.aborted) {
      publishRuntimeRequestState({
        kind: timedOut
          ? 'timeout'
          : typeof navigator !== 'undefined' && navigator.onLine === false
            ? 'offline'
            : 'unavailable',
      });
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
    callerSignal?.removeEventListener('abort', abortFromCaller);
  }
}
